import { SetStateAction, useContext, useEffect, useRef, useState } from "react";
import { Control, Controller, UseFormRegister, UseFormSetValue } from "react-hook-form";
import toast from "react-hot-toast";
import { FaTimes, FaUpload } from "react-icons/fa";
import { CustomLabel } from "shared/components/input/CustomLabel";
import { InputErrorMessage } from "shared/components/input/InputErrorMessage";
import { FileToBase64 } from "shared/util/functions/file/FileFunctions";
import { AnexoItem } from "./InputMultiAnexos";
import { UserContext } from "context/UserContext";
import { Tooltip } from "@material-ui/core";

type InputAnexoProps = {
  id: string;
  name: string;
  control: Control<any>;
  setValue: UseFormSetValue<any>;
  accept: string;
  required?: boolean;
  label?: React.ReactNode;
  placeholder?: string;
  onRemove?: () => void;
  hasInfoAdicional?: boolean;
};

const InputAnexo = ({
  id,
  name,
  label,
  control,
  setValue,
  accept = "",
  required,
  placeholder,
  onRemove,
  hasInfoAdicional = false,
}: InputAnexoProps) => {
  const [fileName, setFileName] = useState<string>("");
  const inputRef = useRef<HTMLInputElement | null>(null);
  const { usuarioLogado } = useContext(UserContext);

  useEffect(() => {
    setFileName(placeholder || "");
  }, [placeholder]);

  const validarExtensaoArquivo = (file: string) => {
    if (accept && accept !== "") {
      const extensoesPermitidas = accept.split(",").map((ext) => ext.trim().toLowerCase());
      const extensaoArquivo = file.substring(file.lastIndexOf(".")).toLowerCase();
      return extensoesPermitidas.includes(extensaoArquivo);
    }
    return true;
  }

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>, onChange?: (val: any) => void) => {
    const file = event.target.files && event.target.files[0];
    if (!file) return;
    if (!validarExtensaoArquivo(file.name)) {
      toast.error("O formato do arquivo enviado é inválido. Extensões válidas .pfx ou .p12.")
      if (inputRef.current) inputRef.current.value = "";
      return;
    }

    setFileName(file.name);
    const base64 = await FileToBase64(file);
    if (!hasInfoAdicional) {
      onChange?.(base64);
      setValue(name, base64, { shouldValidate: true, shouldDirty: true, shouldTouch: true });
      setValue("nomeCertificadoDigital", file.name, { shouldValidate: true, shouldDirty: true, shouldTouch: true });
    } else {
      const novoAnexo: AnexoItem = {
        idAdd: `temp-${Date.now()}-${Math.random()}`,
        nome: file.name,
        anexadoPor: usuarioLogado.nome,
        data: new Date().toISOString(),
        origemLancamento: "GROUP_PAY",
        tipoDocumento: "OUTROS",
        anexoComprovante: false,
        arquivo: base64,
        mimeType: file.type,
      };
      onChange?.(novoAnexo);
      setValue(name, [novoAnexo], { shouldValidate: true, shouldDirty: true, shouldTouch: true });
    }
  };

  const handleRemove = (onChange?: (val: any) => void) => {
    setFileName("");
    if (inputRef.current) inputRef.current.value = "";
    onChange?.("");
    setValue(name, "", { shouldValidate: true, shouldDirty: true, shouldTouch: true });
    setValue("nomeCertificadoDigital", "", { shouldValidate: true, shouldDirty: true, shouldTouch: true });

    onRemove && onRemove();
  };

  return (
    <Controller
      name={name}
      control={control}
      defaultValue=""
      render={({ field, fieldState }) => (
        <>
          <div style={{ display: "flex", flexDirection: "column", width: "100%" }}>
            <CustomLabel>
              {label ? (
                <>
                  {label} {required && <span>*</span>}
                </>
              ) : (
                <span>&nbsp;</span>
              )}
            </CustomLabel>

            <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
              <input
                id={`${id}-input-file`}
                type="file"
                ref={inputRef}
                style={{ display: "none" }}
                accept={accept}
                onChange={(e) => handleFileChange(e, field.onChange)}
                onClick={(e) => (e.currentTarget.value = "")}
                onBlur={field.onBlur}
              />

              <label
                htmlFor={`${id}-input-file`}
                style={{
                  display: "flex",
                  alignItems: "center",
                  padding: "0.53rem 2.5rem 0.53rem 1rem",
                  border: fieldState.error ? "1px solid red" : "1px solid #ccc",
                  borderRadius: "4px",
                  cursor: "pointer",
                  width: "100%",
                  backgroundColor: "#f9f9f9",
                  overflow: "hidden",
                  whiteSpace: "nowrap",
                }}
              >
                <FaUpload style={{
                  marginRight: "0.5rem",
                  flexShrink: 0,
                  position: 'relative',
                  bottom: '2px',
                }} />
                <Tooltip
                  title={fileName || "Escolher arquivo"}
                  placement="top"
                  arrow
                  PopperProps={{ style: { zIndex: 9999 } }}
                >
                  <span style={{
                    flexGrow: 1,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    cursor: 'pointer',
                  }}>
                    {fileName || "Escolher arquivo"}
                  </span>
                </Tooltip>
              </label>

              {((fileName && fileName !== "") || (field.value && field.value !== "")) && (
                <button
                  type="button"
                  onClick={() => handleRemove(field.onChange)}
                  style={{
                    position: "absolute",
                    right: 10,
                    top: "50%",
                    transform: "translateY(-50%)",
                    border: "none",
                    background: "transparent",
                    cursor: "pointer",
                  }}
                  aria-label="Remover arquivo"
                >
                  <FaTimes color="#999" />
                </button>
              )}
            </div>

            {/* erro de validação */}
            {fieldState.error && <InputErrorMessage>{fieldState.error.message}</InputErrorMessage>}
          </div>
        </>
      )}
    />
  );
};

export default InputAnexo;
